import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import {
  BusinessOutlined,
  EmailOutlined,
  InboxOutlined,
  LanguageOutlined,
  LinkedIn,
  LocalPhoneOutlined,
  PersonOutlined,
  QuestionAnswerOutlined,
  WorkspacesOutlined,
} from '@mui/icons-material';
import { fetchCustomerDetails } from '../../../modules/admin';
import TableComponent from '../../elements/TableComponent';
import Label from '../../elements/Label';
import { formatWithCountryCode } from '../../../lib/utils';
import { usersHeaders, projectsHeaders } from '../../../static-data/projects';
import AdminDashboardLayout from '../../../layout/dashboard/AdminDashboardLayout';


const EmptyState = ({ message }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      py: 1.5,
      px: 2,
      borderRadius: 1.5,
      bgcolor: 'grey.100',
      border: '1px dashed',
      borderColor: 'grey.300',
    }}
  >
    <InboxOutlined sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

const InfoField = ({ icon: Icon, label, value, href }) => {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: 1.5,
          bgcolor: 'primary.50',
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        <Icon sx={{ fontSize: 16, color: 'primary.main' }} />
      </Box>
      <Box>
        <Typography variant="caption" sx={{ display: 'block', mb: 0.25 }}>
          {label}
        </Typography>
        {href ? (
          <Typography
            variant="body2"
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'primary.main', fontWeight: 500 }}
          >
            {value}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.primary" fontWeight={500}>
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );
};


const SectionCard = ({ title, icon: Icon, children }) => (
  <Card elevation={1} sx={{ borderRadius: 2, overflow: 'visible', height: '100%' }}>
    <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <Icon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="overline" color="primary.main">
          {title}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2.5 }} />
      {children}
    </CardContent>
  </Card>
);


const TableCard = ({ icon: Icon, title, count, emptyMessage, children }) => (
  <Card elevation={1} sx={{ borderRadius: 2 }}>
    <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="overline" color="primary.main">
            {title}
          </Typography>
        </Box>
        <Chip
          label={`${count} total`}
          size="small"
          sx={{ bgcolor: 'primary.50', color: 'primary.main', fontWeight: 600 }}
        />
      </Box>
      <Divider sx={{ mb: 1 }} />
      {count === 0 ? <EmptyState message={emptyMessage} /> : children}
    </CardContent>
  </Card>
);


const CustomerDetailPage = () => {
  const { organizationId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetchCustomerDetails(organizationId);
        setData(response);
      } catch (err) {
        console.error('Failed to fetch customer details:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [organizationId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Customer not found.</Typography>
      </Box>
    );
  }

  const { organization, admin, users = [], projects = [] } = data;

  const hasOrgData =
    organization.website ||
    organization.industry ||
    organization.linkedin ||
    organization.org_phone_number ||
    organization.evaluation_reason;

  return (
    <AdminDashboardLayout>
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1280, mx: 'auto' }}>

      {/* ── Page Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Avatar sx={{ width: 52, height: 52, bgcolor: 'primary.main', fontSize: '1.25rem', fontWeight: 700 }}>
          {organization.name?.[0]?.toUpperCase()}
        </Avatar>
        <Box>
          <Typography variant="h4" color="text.primary">
            {organization.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Label name={admin?.status || 'unknown'} />
            <Chip
              label={organization.type || 'Free'}
              size="small"
              sx={{ bgcolor: 'primary.50', color: 'primary.main', fontWeight: 600 }}
            />
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>

        {/* ── Organization Details ── */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Organization Details" icon={BusinessOutlined}>
            {hasOrgData ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <InfoField icon={LanguageOutlined} label="Website" value={organization.website} href={organization.website} />
                <InfoField icon={WorkspacesOutlined} label="Industry" value={organization.industry} />
                <InfoField icon={LinkedIn} label="LinkedIn" value={organization.linkedin} href={organization.linkedin} />
                <InfoField
                  icon={LocalPhoneOutlined}
                  label="Organization Phone"
                  value={formatWithCountryCode(organization.org_phone_number, organization.org_country_code)}
                />
                <InfoField icon={QuestionAnswerOutlined} label="Reason for choosing GAiNSS" value={organization.evaluation_reason} />
              </Box>
            ) : (
              <EmptyState message="No organization details have been provided yet." />
            )}
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Organization Admin" icon={PersonOutlined}>
            {admin ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40, fontSize: '0.9rem' }}>
                    {admin.full_name?.[0]?.toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {admin.full_name}
                    </Typography>
                    <Typography variant="caption">Org Admin</Typography>
                  </Box>
                </Box>
                <Divider />
                <InfoField icon={EmailOutlined} label="Email" value={admin.email} href={`mailto:${admin.email}`} />
                <InfoField
                  icon={LocalPhoneOutlined}
                  label="Phone"
                  value={formatWithCountryCode(admin.phone_number, admin.country_code)}
                />
              </Box>
            ) : (
              <EmptyState message="No admin has been assigned to this organization." />
            )}
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <TableCard
            icon={PersonOutlined}
            title="Users"
            count={users.length}
            emptyMessage="No users are currently associated with this organization."
          >
            <TableComponent
              data={users}
              headerTabs={usersHeaders}
              page={1}
              perPage={users.length || 10}
              order="asc"
              setOrder={() => {}}
              defaultSort="full_name"
              isLoading={false}
              totalCount={users.length}
              tablekey="users"
              striped
            />
          </TableCard>
        </Grid>

        <Grid item xs={12}>
          <TableCard
            icon={WorkspacesOutlined}
            title="Projects"
            count={projects.length}
            emptyMessage="No projects have been created for this organization."
          >
            <TableComponent
              data={projects}
              headerTabs={projectsHeaders}
              page={1}
              perPage={projects.length || 10}
              order="asc"
              setOrder={() => {}}
              defaultSort="name"
              isLoading={false}
              totalCount={projects.length}
              tablekey="projects"
              striped
            />
          </TableCard>
        </Grid>

      </Grid>
    </Box>
    </AdminDashboardLayout>
  );
};

export default CustomerDetailPage;